import { isInWeb3Mini, onlyCallOnceInOneTick, runAsyncSafe } from '@web3/utils';
import semverGte from 'semver/functions/gte';
import semverValid from 'semver/functions/valid';
import { WalletTypesNumber } from '../../types';
import { debugLog } from '../../utils/common';
import { BtcConnector } from '../connectors/btc/BtcConnector';
import { Web3ProviderConnector } from '../connectors/evm/Web3ProviderConnector';
import { SolanaConnector } from '../connectors/solana/SolanaConnector';
import { StacksConnector } from '../connectors/stx/StacksConnector';
import { SuiConnector } from '../connectors/sui/SuiConnector';
import { TonConnector } from '../connectors/ton/TonConnector';
import { TronConnector } from '../connectors/tron/TronConnector';
import { ChainTypeList, ChainTypes } from '../core/types';
import { ConnectOption, WalletHub } from '../core/WalletHub';
import { isBrowser } from '../misc';
import { minBybitExtVersion, walletIdMap } from './../../utils/constants';

export const bybitWalletName = 'Bybit Wallet';
/** 现在这个 BybitExtensionHub 其实已经不只是 extension 了，还可以是在app 里用app钱包*/
export class BybitExtensionHub extends WalletHub {
  constructor() {
    const evmConnector = new Web3ProviderConnector('by-ext-evm', 'bybitWallet');
    super({
      name: bybitWalletName,
      icon: '',
      id: walletIdMap.bybit,
      connectors: [
        evmConnector,
        new SolanaConnector('by-ext-solana', () => (isBrowser ? window['bybitWallet']?.solana : undefined)),
        new BtcConnector('by-ext-btc', () => (isBrowser ? window['bybitWallet']?.bitcoin : undefined)),
        new StacksConnector('by-ext-stx', () => (isBrowser ? window['bybitWallet']?.stacks : undefined)),
        new TonConnector('by-ext-ton', () => (isBrowser ? window['bybitTonWallet']?.tonconnect : undefined)),
        new SuiConnector('by-ext-sui', () => (isBrowser ? window['bybitWallet']?.sui : undefined)),
        new TronConnector('by-ext-tron', () => (isBrowser ? window['bybitWallet']?.tronLink : undefined)),
      ],
      installUrl: 'https://chromewebstore.google.com/detail/bybit-wallet/pdliaogehgdbhbnmkklieghmmjkpigpa',
    });

    if (!isInWeb3Mini()) {
      evmConnector.on('walletChange', () => {
        this.updateWalletInfo();
      });
    }
  }

  // 云钱包
  public get isBybitCloud() {
    return this.walletType === 1;
  }
  // 助记词钱包
  public get isMnemonic() {
    return this.walletType === 5;
  }
  // 观察者钱包
  public get isFollow() {
    return this.walletType === 6;
  }

  public walletType: number = 0;

  public walletInfo: BybitExtensionWalletInfo | null = null;

  updateWalletInfo = onlyCallOnceInOneTick(async () => {
    const provider = this.EVM?.provider;
    if (!provider) {
      return;
    }
    debugLog('bybit extension: updating wallet info');
    const [, info] = await runAsyncSafe(provider.request({ method: 'getWalletInfo' }) as Promise<BybitExtensionWalletInfo | null>);
    debugLog('bybit extension updated wallet info', info);
    const { walletType = 0, addressChain } = info || {};
    this.walletInfo = info;
    this.walletType = walletType;
    if (addressChain) {
      // 根据wallet info 返回的地址列表，去恢复对应钱包的
      for (const type of ChainTypeList) {
        const connector = this.getConnector(type);
        if (type === 'EVM' || !connector) {
          continue;
        }
        const address = addressChain.find((a) => a.chainType === ChainTypes[type]);
        if (address) {
          connector._innerUseSetAddress(address.address, address.publicKey);
          if (type === 'BTC') {
            // bybit 插件的 btc 地址类型是 通过这个 chainName返回的。
            connector.addressType = address.chainName;
          }
        } else {
          // 清除地址
          connector._innerUseSetAddress(undefined, undefined);
        }
      }
    } else {
      await this.disconnect();
    }
  });

  override async connect(recovery?: boolean, opt?: ConnectOption): Promise<void> {
    debugLog('bybit extension', recovery ? 'recovery' : 'connect');
    this.walletType = 0;
    await this.initConnectors();
    for (const connector of this.connectors) {
      await connector.getProvider();
    }
    await this.EVM?.connect(recovery, ...(opt?.EVM?.args ?? []));
    if (!isInWeb3Mini()) {
      await this.updateWalletInfo();
    }
    return;
  }
}

export const bybitExtension = new BybitExtensionHub();

export const hasBybitExtension = () => isBrowser && Boolean(window['bybitWallet']);

export const getBybitExtensionVersion = () => {
  if (!isBrowser) {
    return null;
  }
  return semverValid(window['bybitWallet']?.version || '');
};

export const isRequireBybitExtensionUpdate = (_version?: string) => {
  const version = getMinBybitExtensionVersion(_version);
  const currentVersion = getBybitExtensionVersion();
  if (!currentVersion) {
    return true;
  }
  return !semverGte(currentVersion, version);
};

export const getMinBybitExtensionVersion = (version?: string) => {
  if (!version) {
    return minBybitExtVersion;
  }
  return semverGte(version, minBybitExtVersion) ? version : minBybitExtVersion;
};

export type BybitAddressChainItem = {
  accountId: string;
  accountName: string;
  address: string;
  addressChainTypeMap: Record<string, number>;
  avatar: string;
  baseTokenContract: string;
  baseTokenName: string;
  chainCode: string;
  chainId: string;
  chainName: string;
  chainType: number;
  createAt: string;
  gasType: number;
  iconDayUrl: string;
  iconNightUrl: string;
  id: string;
  index: number;
  isBackedUp: boolean;
  isMasterKeyTON: boolean;
  isSupportL1Gas: boolean;
  minTransaction: string;
  name: string;
  networkName: string;
  order: string;
  publicKey: string;
  rpcUrl: string;
  status: string;
  support0x: number;
  supportType: number;
  tmpStatus: string;
  txAddress: string;
  walletId: string;
  walletName: string;
  walletType: number;
};

export type BybitExtensionWalletInfo = {
  accountName: string;
  addressChain: BybitAddressChainItem[];
  walletId: string;
  walletType: WalletTypesNumber;
};
